import { useEffect, useRef, useCallback, useMemo } from 'react';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaSearch, FaTimes } from 'react-icons/fa';
import { MdSearchOff } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { StoreState } from '../../store/createStore';
import RenderItem from './RenderItem';
import { useSidebar } from './SidebarContext';
import UserProfile from './UserProfile';
import { SidebarContainer } from './styles';
import * as tp from './types';

const SideBar = ({ items }: tp.SidebarITF) => {
  const {
    isSidebarOpen,
    toggleSidebar,
    searchTerm,
    setSearchTerm,
    isSearchOpen,
    setIsSearchOpen
  } = useSidebar();
  const { t } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const { user } = useSelector((state: StoreState) => state.auth);

  const titleHoverText = user ? `${user.name} - ${user.email}` : process.env.REACT_APP_NAME;

  // Função para normalizar texto (remove acentos e converte para lowercase)
  const normalizeText = useCallback((text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }, []);

  // Função para verificar se um item ou subitem corresponde à busca
  const matchesSearch = useCallback((item: tp.SidebarItem, term: string): boolean => {
    const normalizedTerm = normalizeText(term);

    // Verifica o título traduzido
    const translatedTitle = t(item.title);
    if (normalizeText(translatedTitle).includes(normalizedTerm)) {
      return true;
    }

    // Verifica o título original
    if (normalizeText(item.title).includes(normalizedTerm)) {
      return true;
    }

    // Verifica as tags
    if (item.tags && item.tags.some(tag => normalizeText(tag).includes(normalizedTerm))) {
      return true;
    }

    return false;
  }, [normalizeText, t]);

  // Função para filtrar itens baseado na busca
  const filterItems = useCallback((itemsList: tp.SidebarItem[], term: string): tp.SidebarItem[] => {
    if (!term.trim()) {
      return itemsList;
    }

    return itemsList.reduce<tp.SidebarItem[]>((acc, item) => {
      // Verifica se o item principal corresponde
      const itemMatches = matchesSearch(item, term);

      // Filtra subitens que correspondem
      const matchingSubItems = item.sidebarSubItems?.filter(subItem =>
        subItem.show && matchesSearch(subItem, term)
      );

      // Se o item principal corresponde ou tem subitens que correspondem
      if (itemMatches || (matchingSubItems && matchingSubItems.length > 0)) {
        const filteredItem: tp.SidebarItem = {
          ...item,
          // Se algum subitem corresponde, mostrar apenas esses subitens
          // Se o item principal corresponde, mostrar todos os subitens originais
          sidebarSubItems: itemMatches
            ? item.sidebarSubItems
            : matchingSubItems,
        };
        acc.push(filteredItem);
      }

      return acc;
    }, []);
  }, [matchesSearch]);

  // Itens filtrados
  const filteredItems = useMemo(() => {
    return filterItems(items, searchTerm);
  }, [items, searchTerm, filterItems]);

  // Foca no input quando abre a busca
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Fecha a busca ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node) &&
        isSearchOpen
      ) {
        setIsSearchOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchOpen, setIsSearchOpen, setSearchTerm]);

  // Fecha a busca com ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isSearchOpen) {
        setIsSearchOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSearchOpen, setIsSearchOpen, setSearchTerm]);

  const handleSearchIconClick = () => {
    setIsSearchOpen(true);
  };

  const handleCloseSearch = () => {
    setIsSearchOpen(false);
    setSearchTerm('');
  };

  // Fechar sidebar ao clicar no overlay (mobile)
  const handleOverlayClick = () => {
    if (window.innerWidth <= 1024) {
      toggleSidebar();
    }
  };

  return (
    <SidebarContainer isSidebarOpen={isSidebarOpen}>
      {/* Overlay para mobile */}
      <div
        className="sidebar-overlay"
        onClick={handleOverlayClick}
      />

      <div className="sidebar-content">
        <div className="sidebar-header">
          {isSearchOpen && isSidebarOpen ? (
            <div className="search-container" ref={searchContainerRef}>
              <div className="sidebar-header-btns">
                <input
                  ref={searchInputRef}
                  type="text"
                  className="search-input"
                  placeholder={t('searchMenu') || 'Buscar menu...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="close-search-btn" onClick={handleCloseSearch}>
                  <FaTimes />
                </button>
                <div className="expand-button">
                  {isSidebarOpen ? (
                    <FaAngleDoubleLeft onClick={toggleSidebar} />
                  ) : (
                    <FaAngleDoubleRight onClick={toggleSidebar} />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              <h1 id="sidebar-title" title={titleHoverText}>
                {process.env.REACT_APP_NAME}
              </h1>
              <div className="sidebar-header-btns">
                {isSidebarOpen && (
                  <button
                    className="search-btn"
                    onClick={handleSearchIconClick}
                    title={t('searchMenu') || 'Buscar menu'}
                  >
                    <FaSearch />
                  </button>
                )}
                <div className="expand-button">
                  {isSidebarOpen ? (
                    <FaAngleDoubleLeft onClick={toggleSidebar} />
                  ) : (
                    <FaAngleDoubleRight onClick={toggleSidebar} />
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="items-container">
          {filteredItems.map((item) => {
            // Verifica se o item deve ser mostrado:
            // Para isso verifica a chave "show" do proprio item
            // e tambem se pelo menos 1 subitem tem show=true
            // pois se nenhum submenu é mostrado, o menu principal tbm nao sera mostrado
            const shouldShowItem =
              item.show &&
              (!item.sidebarSubItems || item.sidebarSubItems.some((subItem) => subItem.show));

            if (shouldShowItem) {
              return (
                <RenderItem
                  item={item}
                  key={item.title}
                  isSidebarOpen={isSidebarOpen}
                  closeSidebar={() => toggleSidebar()}
                  forceShowSubItems={!!searchTerm.trim()}
                />
              );
            }
            return null;
          })}
          {searchTerm.trim() && filteredItems.length === 0 && (
            <div className="no-results-message">
              <MdSearchOff />
              {t('noMenuItemsFound') || 'Nenhum item encontrado'}
            </div>
          )}
        </div>

        <UserProfile />
      </div>
    </SidebarContainer>
  );
};

export default SideBar;
